'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'

function readSelectedFromURL(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  const sp = new URLSearchParams(window.location.search)
  return sp.get('topic_slug') || null
}

let historyPatched = false
function ensureHistoryPatched() {
  if (historyPatched || typeof window === 'undefined') {
    return
  }
  const { pushState, replaceState } = window.history

  const fire = () => window.dispatchEvent(new Event('locationchange'))

  window.history.pushState = function (...args) {
    // @ts-ignore
    const ret = pushState.apply(this, args)
    fire()
    return ret
  } as typeof window.history.pushState

  window.history.replaceState = function (...args) {
    // @ts-ignore
    const ret = replaceState.apply(this, args)
    fire()
    return ret
  } as typeof window.history.replaceState

  window.addEventListener('popstate', fire)
  historyPatched = true
}

function subscribeToLocationChange(cb: () => void) {
  ensureHistoryPatched()
  window.addEventListener('locationchange', cb)
  return () => window.removeEventListener('locationchange', cb)
}

function getSnapshotJSON() {
  return JSON.stringify(readSelectedFromURL())
}

function getServerSnapshotJSON() {
  return 'null'
}

export function useTopicFilter() {
  const router = useRouter()
  const pathname = usePathname()

  const selectedJSON = React.useSyncExternalStore(
    subscribeToLocationChange,
    getSnapshotJSON,
    getServerSnapshotJSON,
  )
  const selected = React.useMemo(() => JSON.parse(selectedJSON) as string | null, [selectedJSON])

  const setSelected = React.useCallback(
    (next: string | null) => {
      const sp = new URLSearchParams(window.location.search)
      sp.delete('topic_slug')

      if (next) {
        sp.set('topic_slug', next)
      }

      const qs = sp.toString()
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [router, pathname],
  )

  const toggle = React.useCallback(
    (slug: string) => {
      if (!slug) {
        return
      }
      setSelected(selected === slug ? null : slug)
    },
    [selected, setSelected],
  )

  const clear = React.useCallback(() => setSelected(null), [setSelected])

  return { selected, setSelected, toggle, clear }
}
